/* ==========================================================================
   Internal Layout Component
   Task F36: Layout wrapper for internal (super-admin) pages
   Modeled after AdminLayout.tsx with sidebar navigation
   ========================================================================== */

import { useCallback, useState } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { BarChart3, ClipboardList, CreditCard, Menu, ShieldCheck, X } from 'lucide-react'
import { LogoutButton } from '@/components/ui/LogoutButton'
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { Fragment } from 'react'
import { internalLogout } from '@/api/internal'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface NavItem {
  id: string
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
}

/* --------------------------------------------------------------------------
   Navigation Items
   -------------------------------------------------------------------------- */

const NAV_ITEMS: NavItem[] = [
  {
    id: 'intake',
    label: 'Intake Submissions',
    path: '/internal/intake',
    icon: ClipboardList,
  },
  {
    id: 'checkout',
    label: 'Generate Checkout',
    path: '/internal/checkout',
    icon: CreditCard,
  },
  {
    id: 'metrics',
    label: 'Metrics',
    path: '/internal/metrics',
    icon: BarChart3,
  },
  {
    id: 'security',
    label: 'Security',
    path: '/internal/security',
    icon: ShieldCheck,
  },
]

/* --------------------------------------------------------------------------
   Internal Header
   -------------------------------------------------------------------------- */

interface InternalHeaderProps {
  onLogout: () => void
  onMobileMenuClick: () => void
}

function InternalHeader({ onLogout, onMobileMenuClick }: InternalHeaderProps) {
  return (
    <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onMobileMenuClick}
          className="lg:hidden p-2 -ml-2 rounded-md text-charcoal hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-lavender"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2">
          <Link to="/internal" className="cursor-pointer">
            <span className="font-serif text-xl text-deep-purple">TJM Labs</span>
          </Link>
          <span className="hidden sm:inline text-gray-300">|</span>
          <span className="hidden sm:inline text-sm text-charcoal/70">
            Internal Tools
          </span>
        </div>
      </div>

      <LogoutButton onClick={onLogout} />
    </header>
  )
}

/* --------------------------------------------------------------------------
   Sidebar Nav
   -------------------------------------------------------------------------- */

interface SidebarNavProps {
  items: NavItem[]
  currentPath: string
  onNavigate: (path: string) => void
}

function SidebarNav({ items, currentPath, onNavigate }: SidebarNavProps) {
  return (
    <nav className="flex flex-col gap-1 p-4">
      {items.map((item) => {
        const isActive =
          item.id === 'intake'
            ? currentPath.startsWith('/internal/intake') ||
              currentPath === '/internal'
            : currentPath.startsWith(item.path)
        const Icon = item.icon

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.path)}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full
              transition-colors focus:outline-none focus:ring-2 focus:ring-lavender
              ${
                isActive
                  ? 'bg-soft-lavender text-deep-purple font-medium'
                  : 'text-charcoal hover:bg-gray-100'
              }
            `}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon
              className={`h-5 w-5 flex-shrink-0 ${
                isActive ? 'text-deep-purple' : 'text-gray-500'
              }`}
              aria-hidden="true"
            />
            <span className="text-sm">{item.label}</span>
          </button>
        )
      })}
    </nav>
  )
}

/* --------------------------------------------------------------------------
   Mobile Sidebar Drawer
   -------------------------------------------------------------------------- */

interface MobileSidebarProps {
  open: boolean
  onClose: () => void
  items: NavItem[]
  currentPath: string
  onNavigate: (path: string) => void
}

function MobileSidebar({
  open,
  onClose,
  items,
  currentPath,
  onNavigate,
}: MobileSidebarProps) {
  const handleNavigate = (path: string) => {
    onNavigate(path)
    onClose()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50 lg:hidden">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in duration-150"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <DialogPanel className="fixed inset-y-0 left-0 w-60 bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <span className="font-serif text-lg text-deep-purple block">
                  Internal Tools
                </span>
                <span className="text-xs text-charcoal/60 block">
                  TJM Labs
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-md text-gray-500 hover:text-charcoal hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-lavender"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <SidebarNav
              items={items}
              currentPath={currentPath}
              onNavigate={handleNavigate}
            />
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}

/* --------------------------------------------------------------------------
   Main Internal Layout
   -------------------------------------------------------------------------- */

export function InternalLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
    },
    [navigate]
  )

  const handleLogout = useCallback(async () => {
    await internalLogout()
    navigate('/internal/login', { replace: true })
  }, [navigate])

  return (
    <div className="min-h-screen bg-warm-beige">
      <InternalHeader
        onLogout={handleLogout}
        onMobileMenuClick={() => setMobileMenuOpen(true)}
      />

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 bg-white border-r border-gray-200 min-h-[calc(100vh-64px)]">
          <SidebarNav
            items={NAV_ITEMS}
            currentPath={location.pathname}
            onNavigate={handleNavigate}
          />
        </aside>

        {/* Mobile sidebar */}
        <MobileSidebar
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          items={NAV_ITEMS}
          currentPath={location.pathname}
          onNavigate={handleNavigate}
        />

        {/* Main content */}
        <main className="flex-1 min-h-[calc(100vh-64px)]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default InternalLayout
